import { useState, useCallback } from 'react';
import { chatApi } from '../services/api/chat.api';
import { useAuth } from './useAuth';
import type { Message } from '../types/chat.types';

export const useChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checkQuota } = useAuth();

  const sendMessage = useCallback(
    async (content: string): Promise<void> => {
      if (!content.trim()) return;

      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Pass conversation history (messages before current user message)
        const response = await chatApi.sendMessage(content.trim(), messages);

        // Add assistant message with new citation structure
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.answer,
          timestamp: new Date(),
          inline_citations: response.inline_citations,
          sources: response.sources,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Update quota
        await checkQuota();
      } catch (err: any) {
        // Extract error message properly
        let errorMessage = 'Failed to send message';

        if (err && typeof err === 'object') {
          if (typeof err.message === 'string') {
            errorMessage = err.message;
          } else if (err.detail) {
            // Handle FastAPI validation errors (422)
            if (Array.isArray(err.detail)) {
              errorMessage = err.detail.map((e: any) => e.msg).join(', ');
            } else if (typeof err.detail === 'string') {
              errorMessage = err.detail;
            }
          }
        }

        setError(errorMessage);
        console.error('Error sending message:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, checkQuota]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
};
